import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import VaccinesIcon from '@mui/icons-material/Vaccines'
import EditNoteIcon from '@mui/icons-material/EditNote'
import moment from 'moment'
import API from '../../../../services/API'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import AdministrationSelect from '../../../governance/medsMx/AdministrationSelect'
import WitnessPicker from '../../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
} from '../../../../helpers/medicineWitness'
import useLiveRecord from './useLiveRecord'
import { replaceEntry } from './engine'

const DOSE_UNITS = ['mg', 'g', 'mcg', 'ml']
const ROUTES = [
  'Intravenous',
  'Intraosseous',
  'Intramuscular',
  'Intranasal',
  'Subcutaneous',
  'Buccal',
  'Oral',
  'Rectal',
  'Nebulised',
]

const parseDose = (text) => {
  const match = String(text ?? '').match(/^\s*([\d.]+)\s*(mg|g|mcg|ml)\b/i)
  return match ? { amount: match[1], unit: match[2].toLowerCase() } : null
}

const routeFromShort = (short) => {
  const value = String(short ?? '').toUpperCase()
  if (value.startsWith('IV')) return 'Intravenous'
  if (value === 'IO') return 'Intraosseous'
  if (value.startsWith('IM')) return 'Intramuscular'
  if (value === 'IN') return 'Intranasal'
  if (value === 'SC') return 'Subcutaneous'
  if (value === 'BUCCAL') return 'Buccal'
  if (value === 'PO') return 'Oral'
  if (value.startsWith('PR')) return 'Rectal'
  if (value === 'NEBULISED') return 'Nebulised'
  return ''
}

const OptionCard = ({
  icon: Icon,
  title,
  caption,
  selected,
  onClick,
  disabled,
}) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      flex: 1,
      minHeight: 72,
      borderRadius: 2,
      border: 2,
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? 'action.selected' : 'background.paper',
      p: 1.5,
      justifyContent: 'flex-start',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <Stack direction="row" gap={1.5} alignItems="center" textAlign="left">
      <Icon color="primary" />
      <Stack>
        <Typography fontWeight={700}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Stack>
    </Stack>
  </ButtonBase>
)

// Drawn-up (prepared) syringes held by the signed-in clinician
const PreparedPicker = ({ entry, values, onLinked }) => {
  const { formID, user } = useLiveRecord()
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [prepared, setPrepared] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const initialDose = parseDose(values?.Dose)
  const [totalDose, setTotalDose] = useState(initialDose?.amount ?? '')
  const [totalDoseType, setTotalDoseType] = useState(initialDose?.unit ?? 'mg')
  const [route, setRoute] = useState(routeFromShort(values?.Route))
  const [witness, setWitness] = useState(emptyWitness())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    API.get('/medsmx/prepared/mine', { skipGlobalErrorHandler: true })
      .then((res) => {
        if (!active) return
        if (res.status === 200 && Array.isArray(res.data)) {
          setPrepared(res.data)
          const wanted = (values?.['Medicine Name'] ?? '').toLowerCase()
          const match = res.data.find((item) =>
            wanted
              ? (item.medicineName ?? '').toLowerCase().includes(wanted)
              : false,
          )
          if (match) setSelectedId(match._id)
        } else {
          setUnavailable(true)
        }
      })
      .catch(() => active && setUnavailable(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  const selected = prepared.find((item) => item._id === selectedId)

  useEffect(() => {
    if (!selected || totalDose) return
    const dose = parseDose(selected.dose)
    if (dose) {
      setTotalDose(dose.amount)
      setTotalDoseType(dose.unit)
    }
  }, [selected])

  const canSubmit =
    selected &&
    totalDose &&
    route &&
    (!selected.isControlled || isWitnessComplete(witness)) &&
    !submitting

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await API.post(
        `/medsmx/prepared/${selected._id}/administer`,
        {
          epcrID: formID,
          administeredTime: entry?.dateTime ?? new Date(),
          totalDose,
          totalDoseType,
          route,
          administeredBy: user?._id,
          witness: selected.isControlled
            ? toWitnessPayload(witness)
            : undefined,
        },
        { skipGlobalErrorHandler: true },
      )
      if (res.status === 200 && res.data?.entry) {
        onLinked(res.data.entry)
      } else {
        setError(
          res.data?.error ??
          res.data?.errorMessage ??
            'The drawn-up medicine could not be recorded. Please try again.',
        )
      }
    } catch (e) {
      setError('The drawn-up medicine could not be recorded. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <CircularProgress size="1.5rem" />
  if (unavailable)
    return <Alert severity="info">Drawn-up medicines aren't available.</Alert>
  if (!prepared.length)
    return (
      <Alert severity="info">
        You aren't holding any drawn-up medicines. Draw them up from your drug
        bag first, or record this medicine another way.
      </Alert>
    )

  return (
    <Stack gap={1.5}>
      <Stack gap={1}>
        {prepared.map((item) => (
          <ButtonBase
            key={item._id}
            onClick={() => setSelectedId(item._id)}
            sx={{
              border: 2,
              borderRadius: 2,
              p: 1.5,
              justifyContent: 'flex-start',
              borderColor: item._id === selectedId ? 'primary.main' : 'divider',
              bgcolor:
                item._id === selectedId ? 'action.selected' : 'transparent',
            }}
          >
            <Stack textAlign="left" width="100%">
              <Typography fontWeight={700}>
                {item.medicineName}
                {item.isControlled ? ' (CD)' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[item.dose, item.volumeMl ? `${item.volumeMl}ml` : null, item.concentration]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Drawn {moment(item.preparedAt).format('DD/MM HH:mm')}
                {item.fromLocation?.name
                  ? ` from ${item.fromLocation.name}`
                  : ''}
                {item.expiresAt
                  ? ` · use by ${moment(item.expiresAt).format('DD/MM HH:mm')}`
                  : ''}
              </Typography>
            </Stack>
          </ButtonBase>
        ))}
      </Stack>
      {selected && (
        <Stack gap={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <TextField
              label="Dose given"
              value={totalDose}
              type="number"
              inputProps={{ inputMode: 'decimal' }}
              onChange={(e) => setTotalDose(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      variant="standard"
                      value={totalDoseType}
                      onChange={(e) => setTotalDoseType(e.target.value)}
                    >
                      {DOSE_UNITS.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Route</InputLabel>
              <Select
                label="Route"
                value={route}
                onChange={(e) => setRoute(e.target.value)}
              >
                {ROUTES.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {selected.isControlled && (
            <Stack gap={1}>
              <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                Controlled drug - the administration must be witnessed.
              </Alert>
              <WitnessPicker
                value={witness}
                onChange={setWitness}
                size="medium"
              />
            </Stack>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            variant="contained"
            color="success"
            size="large"
            disabled={!canSubmit}
            onClick={submit}
          >
            {submitting ? 'Recording...' : 'Record administration'}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

// "Link to stock" panel shown at the top of medicine detail forms. Either
// path swaps the stub entry for the tracked 'Medicine Administration' entry
// (engine.replaceEntry) so the timeline link survives and nothing is
// duplicated. `onLinked` closes the detail form.
const MedicineLinkPanel = ({ entry, values, onLinked }) => {
  const { formID, updateRecord } = useLiveRecord()
  const { epcrPracticeMode } = useContext(SiteModeContext)
  const [mode, setMode] = useState('manual')
  const [bagOpen, setBagOpen] = useState(false)
  const canTrack = !!formID && !epcrPracticeMode && !!entry?.entryId

  const link = (moduleEntry) => {
    updateRecord((record) =>
      replaceEntry(record, 'treatment', entry.entryId, {
        moduleName: 'Medicine Administration',
        ...moduleEntry,
      }),
    )
    setBagOpen(false)
    onLinked()
  }

  return (
    <Stack gap={1.5}>
      <Typography fontWeight={700}>Link to stock</Typography>
      {!canTrack && (
        <Alert severity="info" sx={{ py: 0 }}>
          {epcrPracticeMode
            ? 'Stock can not be used in practice mode.'
            : 'The record needs to save before stock can be linked - wait a few seconds and try again.'}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <OptionCard
          icon={MedicalServicesIcon}
          title="From a drug bag"
          caption="Pick the bag, medicine and batch"
          disabled={!canTrack}
          selected={mode === 'bag'}
          onClick={() => {
            setMode('bag')
            setBagOpen(true)
          }}
        />
        <OptionCard
          icon={VaccinesIcon}
          title="Drawn-up syringe"
          caption="A medicine you drew up earlier"
          disabled={!canTrack}
          selected={mode === 'prepared'}
          onClick={() => setMode('prepared')}
        />
        <OptionCard
          icon={EditNoteIcon}
          title="Record manually"
          caption="Fill in the details below"
          selected={mode === 'manual'}
          onClick={() => setMode('manual')}
        />
      </Stack>
      {mode === 'prepared' && canTrack && (
        <Box>
          <PreparedPicker entry={entry} values={values} onLinked={link} />
        </Box>
      )}
      <Dialog
        open={bagOpen}
        onClose={() => setBagOpen(false)}
        fullWidth
        maxWidth="md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Administer from a drug bag</DialogTitle>
        <DialogContent>
          {bagOpen && (
            <AdministrationSelect
              epcrID={formID}
              prefill={{
                medicineName: values?.['Medicine Name'],
                dose: values?.Dose,
                time: entry?.dateTime,
              }}
              submitEPCRModule={(moduleName, moduleData) =>
                link({ ...moduleData, moduleName })
              }
              setSelectedModule={() => setBagOpen(false)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBagOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default MedicineLinkPanel
